import { useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'
import {FileText, Clock, CheckCircle, XCircle, SquareCheck as CheckSquare, TrendingUp, Calendar, ArrowUpRight, Layers, } from 'lucide-react'
import { formatarData } from '@/utils/protocolo'
import type { Solicitacao, StatusSolicitacao } from '@/types'
import { STATUS_COLORS } from '@/types'

const BRAND      = '#0f3b50'
const BRAND_LIGHT = '#e8f1f5'

// ── Logo GEOA ─────────────────────────────────────────────────────────────────
function GeoaLogo({ size = 32 }: { size?: number }) {
  return (
    <img
      src="https://static.lumi.new/24/2440432f58c0c7516f7c4599415c87d6.webp"
      alt="GEOA"
      width={size}
      height={size}
      style={{ objectFit: 'contain' }}
    />
  )
}

interface Stats {
  total: number
  pendentes: number
  aprovadas: number
  reprovadas: number
  concluidas: number
}

interface BairroCount { nome: string; count: number }
interface MesCount    { mes: string;  count: number }
interface StatusCount { status: string; count: number }

export default function DashboardPage() {
  const [loading, setLoading]     = useState(true)
  const [stats, setStats]         = useState<Stats>({ total: 0, pendentes: 0, aprovadas: 0, reprovadas: 0, concluidas: 0 })
  const [porBairro, setPorBairro] = useState<BairroCount[]>([])
  const [porMes, setPorMes]       = useState<MesCount[]>([])
  const [porStatus, setPorStatus] = useState<StatusCount[]>([])
  const [recentes, setRecentes]   = useState<Solicitacao[]>([])

  useEffect(() => {
    lumi.entities.solicitacoes.list({ limit: 500, sort: { createdAt: -1 } }).then((r) => {
      const list = (r.list || []) as Solicitacao[]
      computeStats(list)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const computeStats = (list: Solicitacao[]) => {
    const statusPendentes: StatusSolicitacao[] = [
      'Solicitação Recebida', 'Em Análise', 'Vistoria Agendada', 'Vistoria Realizada', 'Aguardando Replantio',
    ]
    setStats({
      total:     list.length,
      pendentes: list.filter(s => statusPendentes.includes(s.status)).length,
      aprovadas: list.filter(s => s.status === 'Aprovada').length,
      reprovadas:list.filter(s => s.status === 'Reprovada').length,
      concluidas:list.filter(s => s.status === 'Processo Concluído').length,
    })

    const bairroMap: Record<string, number> = {}
    list.forEach(s => { bairroMap[s.bairroNome] = (bairroMap[s.bairroNome] || 0) + 1 })
    setPorBairro(
      Object.entries(bairroMap)
        .map(([nome, count]) => ({ nome, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6)
    )

    const mesMap: Record<string, number> = {}
    const now = new Date()
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      mesMap[d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })] = 0
    }
    list.forEach(s => {
      const key = new Date(s.createdAt).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
      if (key in mesMap) mesMap[key]++
    })
    setPorMes(Object.entries(mesMap).map(([mes, count]) => ({ mes, count })))

    const statusMap: Record<string, number> = {}
    list.forEach(s => { statusMap[s.status] = (statusMap[s.status] || 0) + 1 })
    setPorStatus(Object.entries(statusMap).map(([status, count]) => ({ status, count })))

    setRecentes(list.slice(0, 6))
  }

  const maxBairro = Math.max(...porBairro.map(b => b.count), 1)
  const maxMes    = Math.max(...porMes.map(m => m.count), 1)

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-3">
          <div className="w-14 h-14 rounded-3xl mx-auto flex items-center justify-center" style={{ backgroundColor: BRAND_LIGHT }}>
            <GeoaLogo size={36} />
          </div>
          <p className="text-sm text-gray-400 font-medium">A carregar dashboard…</p>
        </div>
      </div>
    )
  }

  // ── Cards de métricas ──────────────────────────────────────────────────────
  const metricCards = [
    { label: 'Total',      value: stats.total,      icon: FileText,    accent: BRAND,     bg: BRAND_LIGHT,  bar: BRAND },
    { label: 'Pendentes',  value: stats.pendentes,  icon: Clock,       accent: '#d97706', bg: '#fef3c7',    bar: '#f59e0b' },
    { label: 'Aprovadas',  value: stats.aprovadas,  icon: CheckCircle, accent: '#059669', bg: '#d1fae5',    bar: '#10b981' },
    { label: 'Reprovadas', value: stats.reprovadas, icon: XCircle,     accent: '#dc2626', bg: '#fee2e2',    bar: '#ef4444' },
    { label: 'Concluídas', value: stats.concluidas, icon: CheckSquare, accent: '#4b5563', bg: '#f3f4f6',    bar: '#6b7280' },
  ]

  return (
    <div className="space-y-6">

      {/* ── Cabeçalho ──────────────────────────────────────────────────────── */}
      <div
        className="rounded-3xl px-6 py-5 flex items-center justify-between gap-4 overflow-hidden relative"
        style={{ backgroundColor: BRAND }}
      >
        {/* Círculo decorativo */}
        <div
          className="absolute -top-8 -right-8 w-40 h-40 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)' }}
        />
        <div className="flex items-center gap-4 relative z-10">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
            <GeoaLogo size={30} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white leading-tight">Dashboard</h1>
            <p className="text-xs text-white/60 mt-0.5">GEOA – Gestão Online Ambiental</p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 bg-white/10 border border-white/15 rounded-2xl px-4 py-2.5 relative z-10">
          <Layers className="w-4 h-4 text-white/70" />
          <div>
            <p className="text-white text-lg font-extrabold leading-none">{stats.total}</p>
            <p className="text-white/50 text-[10px] font-medium mt-0.5 uppercase tracking-wide">Total de processos</p>
          </div>
        </div>
      </div>

      {/* ── Cards de métricas ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {metricCards.map((card) => {
          const Icon = card.icon
          return (
            <div
              key={card.label}
              className="bg-white rounded-3xl border border-gray-100 p-4 shadow-sm flex flex-col justify-between min-h-[110px] overflow-hidden relative"
            >
              {/* Barra de acento no topo */}
              <div className="absolute top-0 left-0 right-0 h-1 rounded-t-3xl" style={{ backgroundColor: card.bar }} />
              <div className="flex items-start justify-between mt-1">
                <div
                  className="w-9 h-9 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: card.bg }}
                >
                  <Icon style={{ color: card.accent, width: 17, height: 17 }} />
                </div>
                <ArrowUpRight className="w-3.5 h-3.5 text-gray-300" />
              </div>
              <div className="mt-3">
                <p className="text-2xl font-extrabold text-gray-900 leading-none">{card.value}</p>
                <p className="text-xs text-gray-400 font-medium mt-1">{card.label}</p>
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Gráficos ───────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Por Bairro */}
        <div className="bg-white rounded-3xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-7 h-7 rounded-xl flex items-center justify-center" style={{ backgroundColor: BRAND_LIGHT }}>
              <TrendingUp className="w-3.5 h-3.5" style={{ color: BRAND }} />
            </div>
            <h2 className="text-sm font-semibold text-gray-900">Solicitações por Bairro</h2>
          </div>
          {porBairro.length === 0 ? (
            <p className="text-sm text-gray-300 text-center py-8">Nenhum dado disponível</p>
          ) : (
            <div className="space-y-3.5">
              {porBairro.map((b) => {
                const pct = Math.round((b.count / maxBairro) * 100)
                return (
                  <div key={b.nome}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-gray-700 font-medium truncate max-w-[70%]">{b.nome}</span>
                      <span className="text-gray-400 font-semibold tabular-nums">{b.count}</span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, backgroundColor: BRAND }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Por Mês */}
        <div className="bg-white rounded-3xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-7 h-7 rounded-xl flex items-center justify-center" style={{ backgroundColor: BRAND_LIGHT }}>
              <Calendar className="w-3.5 h-3.5" style={{ color: BRAND }} />
            </div>
            <h2 className="text-sm font-semibold text-gray-900">Solicitações por Mês</h2>
          </div>
          <div className="flex items-end gap-2" style={{ height: 120 }}>
            {porMes.map((m) => {
              const barH = Math.max((m.count / maxMes) * 90, m.count > 0 ? 6 : 0)
              return (
                <div key={m.mes} className="flex-1 flex flex-col items-center gap-1">
                  {m.count > 0 && (
                    <span className="text-[10px] text-gray-500 font-semibold tabular-nums">{m.count}</span>
                  )}
                  <div className="w-full rounded-t-lg" style={{ height: 90, display: 'flex', alignItems: 'flex-end' }}>
                    <div
                      className="w-full rounded-t-lg transition-all duration-700"
                      style={{
                        height: `${barH}px`,
                        backgroundColor: barH > 0 ? BRAND : '#f3f4f6',
                        minHeight: barH > 0 ? 6 : 0,
                      }}
                    />
                  </div>
                  <span className="text-[10px] text-gray-400 text-center leading-tight capitalize">{m.mes}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* ── Por Status + Últimas Solicitações ──────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Distribuição por Status */}
        <div className="bg-white rounded-3xl border border-gray-100 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Distribuição por Status</h2>
          {porStatus.length === 0 ? (
            <p className="text-sm text-gray-300 text-center py-8">Nenhum dado</p>
          ) : (
            <div className="space-y-2">
              {porStatus.map((s) => {
                const cls = STATUS_COLORS[s.status as StatusSolicitacao] || 'bg-gray-50 text-gray-700 border-gray-200'
                const pct = Math.round((s.count / (stats.total || 1)) * 100)
                return (
                  <div key={s.status} className="flex items-center gap-3">
                    <span className={`text-xs px-2.5 py-1 rounded-full border font-medium flex-shrink-0 ${cls}`}>
                      {s.status}
                    </span>
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: BRAND }}
                      />
                    </div>
                    <span className="text-sm font-bold text-gray-800 tabular-nums w-6 text-right flex-shrink-0">
                      {s.count}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Últimas Solicitações */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50">
            <h2 className="text-sm font-semibold text-gray-900">Últimas Solicitações</h2>
          </div>
          {recentes.length === 0 ? (
            <p className="text-sm text-gray-300 text-center py-10">Nenhuma solicitação</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: '#f0f5f8' }}>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wide px-5 py-2.5"
                    style={{ color: BRAND }}>
                    Protocolo
                  </th>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wide px-4 py-2.5 hidden sm:table-cell"
                    style={{ color: BRAND }}>
                    Solicitante
                  </th>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wide px-4 py-2.5 hidden md:table-cell"
                    style={{ color: BRAND }}>
                    Data
                  </th>
                  <th className="text-right text-[10px] font-semibold uppercase tracking-wide px-5 py-2.5"
                    style={{ color: BRAND }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentes.map((s, i) => {
                  const cls = STATUS_COLORS[s.status] || 'bg-gray-50 text-gray-700 border-gray-200'
                  return (
                    <tr
                      key={s._id}
                      className="border-t border-gray-50 hover:bg-gray-50/60 transition"
                      style={i % 2 === 0 ? {} : { backgroundColor: '#fafbfc' }}
                    >
                      <td className="px-5 py-3">
                        <p className="text-xs font-mono font-bold text-gray-900">{s.protocolo}</p>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <p className="text-xs text-gray-600 truncate max-w-[140px]">{s.nomeCompleto}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <p className="text-xs text-gray-400">{formatarData(s.createdAt)}</p>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold whitespace-nowrap ${cls}`}>
                          {s.status.length > 14 ? s.status.slice(0, 14) + '…' : s.status}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </div>
  )
}
